import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";

// Build business-scoped forms path
const businessFormsRef = (businessId) => collection(db, "businesses", businessId, "forms");
const businessFormDoc = (businessId, formId) => doc(db, "businesses", businessId, "forms", formId);
const globalFormDoc = (formId) => doc(db, "forms", formId);

// Direct lookup for /businessSlug/formSlug. Slugs never contain "__".
const formRouteRef = (businessSlug, formSlug) => doc(db, "formRoutes", `${businessSlug}__${formSlug}`);

const saveFormRoute = async (businessSlug, formSlug, formId, businessId) => {
    if (!businessSlug || !formSlug || !formId || !businessId) return;
    await setDoc(formRouteRef(businessSlug, formSlug), {
        formId,
        businessId,
        businessSlug,
        formSlug,
    });
};

const removeFormRoute = async (businessSlug, formSlug) => {
    if (!businessSlug || !formSlug) return;
    try {
        await deleteDoc(formRouteRef(businessSlug, formSlug));
    } catch (error) {
        console.warn("Could not delete form route:", error);
    }
};

/**
 * Point read for a public form URL. Returns null when the route has not been indexed yet.
 */
export const getFormByRoute = async (businessSlug, formSlug) => {
    if (!businessSlug || !formSlug) return null;
    try {
        const routeSnap = await getDoc(formRouteRef(businessSlug, formSlug));
        if (!routeSnap.exists()) return null;
        const formId = routeSnap.data()?.formId;
        if (!formId) return null;
        const formSnap = await getDoc(globalFormDoc(formId));
        if (!formSnap.exists()) return null;
        return { ...formSnap.data(), id: formSnap.id };
    } catch (error) {
        console.warn("Form route lookup failed:", error);
        return null;
    }
};

/** Create missing route docs so the next public visit can skip the slug query. */
export const ensureFormRoutes = async (forms) => {
    await Promise.all((forms || []).map(async (form) => {
        if (!form?.id || !form.businessSlug || !form.formSlug || !form.businessId) return;
        try {
            const ref = formRouteRef(form.businessSlug, form.formSlug);
            const snap = await getDoc(ref);
            if (snap.exists() && snap.data()?.formId === form.id) return;
            await saveFormRoute(form.businessSlug, form.formSlug, form.id, form.businessId);
        } catch (error) {
            console.warn("Could not save form route:", error);
        }
    }));
};

// List forms for a business
export const listForms = async (businessId) => {
    const snap = await getDocs(businessFormsRef(businessId));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Save new form under business and mirror to global
export const createForm = async (businessId, data) => {
    const ref = await addDoc(businessFormsRef(businessId), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    await setDoc(globalFormDoc(ref.id), { ...data, updatedAt: serverTimestamp() }, { merge: true });
    try {
        await saveFormRoute(data.businessSlug, data.formSlug, ref.id, businessId);
    } catch (error) {
        console.warn("Could not save form route:", error);
    }
    return ref.id;
};

// Update existing form and mirror to global
export const updateForm = async (businessId, formId, data) => {
    let previous = null;
    try {
        const oldSnap = await getDoc(globalFormDoc(formId));
        if (oldSnap.exists()) previous = oldSnap.data();
    } catch (error) {
        console.warn("Could not read previous form route:", error);
    }

    const ref = businessFormDoc(businessId, formId);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    await setDoc(globalFormDoc(formId), { ...data, updatedAt: serverTimestamp() }, { merge: true });

    try {
        const slugChanged = previous
            && (previous.businessSlug !== data.businessSlug || previous.formSlug !== data.formSlug);
        if (slugChanged) {
            await removeFormRoute(previous.businessSlug, previous.formSlug);
        }
        if (data.businessSlug && data.formSlug) {
            await saveFormRoute(data.businessSlug, data.formSlug, formId, businessId);
        }
    } catch (error) {
        console.warn("Could not update form route:", error);
    }
    return formId;
};

// Publish toggle helpers
export const publishForm = async (businessId, formId) => {
    const payload = { status: "published", publishedAt: serverTimestamp() };
    await setDoc(businessFormDoc(businessId, formId), payload, { merge: true });
    await setDoc(globalFormDoc(formId), payload, { merge: true });
};

export const unpublishForm = async (businessId, formId) => {
    const payload = { status: "draft", publishedAt: null };
    await setDoc(businessFormDoc(businessId, formId), payload, { merge: true });
    await setDoc(globalFormDoc(formId), payload, { merge: true });
};

export const deleteForm = async (businessId, formId) => {
    let previous = null;
    try {
        const oldSnap = await getDoc(globalFormDoc(formId));
        if (oldSnap.exists()) previous = oldSnap.data();
    } catch (error) {
        console.warn("Could not read form before delete:", error);
    }
    await deleteDoc(businessFormDoc(businessId, formId));
    await deleteDoc(globalFormDoc(formId));
    if (previous?.businessSlug && previous?.formSlug) {
        await removeFormRoute(previous.businessSlug, previous.formSlug);
    }
};

// Check if a form slug already exists for a business (excluding the current form if editing)
export const checkFormSlugExists = async (businessId, formSlug, businessSlug, excludeFormId = null) => {
    if (!businessId || !formSlug || !businessSlug) return false;
    
    try {
        try {
            const routeSnap = await getDoc(formRouteRef(businessSlug, formSlug));
            if (routeSnap.exists()) {
                const routeFormId = routeSnap.data()?.formId;
                if (excludeFormId && routeFormId === excludeFormId) return false;
                return true;
            }
        } catch (routeError) {
            console.warn("Form route check failed, falling back to query:", routeError);
        }

        // Query forms collection by businessSlug and formSlug for routes not indexed yet
        const formsQuery = query(
            collection(db, "forms"),
            where("businessSlug", "==", businessSlug),
            where("formSlug", "==", formSlug)
        );
        const formsSnap = await getDocs(formsQuery);
        
        // If we're editing a form, exclude it from the check
        if (excludeFormId) {
            const conflictingForms = formsSnap.docs.filter(doc => doc.id !== excludeFormId);
            return conflictingForms.length > 0;
        }
        
        return !formsSnap.empty;
    } catch (error) {
        console.error("Error checking form slug:", error);
        // If there's an error, assume it doesn't exist to avoid blocking saves
        return false;
    }
};


